import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { FoundingStatusBadge } from "./FoundingStatusBadge";
import { currentPercent, deriveFoundingStatus, type FoundingReportRow } from "./types";

// Leaflet's default marker icons are resolved relative to the CSS at runtime,
// which breaks under a Vite bundle (the images 404). Point the default icon at
// the bundler-hashed asset URLs instead, so markers render everywhere.
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const OSM_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/**
 * A row is only mappable when both coordinates are real, in-range, and not the
 * [0,0] "null island" default that unset coordinates collapse to. Filtering
 * here keeps the map from framing bogus points or rendering a broken marker.
 */
function hasUsableCoordinates(row: FoundingReportRow): boolean {
  const { latitude, longitude } = row;
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180 &&
    !(latitude === 0 && longitude === 0)
  );
}

interface FoundingPropertiesMapProps {
  readonly rows: readonly FoundingReportRow[];
}

export function FoundingPropertiesMap({ rows }: FoundingPropertiesMapProps) {
  const { t } = useTranslation();
  const mappable = rows.filter(hasUsableCoordinates);

  if (mappable.length === 0) {
    return (
      <p
        data-testid="founding-map-empty"
        className="rounded-lg border border-border bg-muted/30 px-3 py-8 text-center font-sans text-sm text-muted-foreground"
      >
        {t("founding.report.mapEmpty")}
      </p>
    );
  }

  // Frame the map on the data rather than a hard-coded center/zoom.
  const bounds = L.latLngBounds(
    mappable.map((row) => [row.latitude, row.longitude] as [number, number]),
  );

  return (
    // MapContainer's react-leaflet type doesn't accept data-* / aria-* props, so
    // the accessible label + test hook live on this wrapper — which is what the
    // page treats as the map region.
    <div
      data-testid="founding-report-map"
      role="region"
      aria-label={t("founding.report.mapLabel")}
      className="overflow-hidden rounded-lg border border-border"
    >
      <MapContainer
        bounds={bounds}
        boundsOptions={{ padding: [40, 40] }}
        scrollWheelZoom={false}
        className="h-[500px] w-full"
      >
        <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} maxZoom={19} />
        {mappable.map((row) => {
          const pct = currentPercent(row.founding);
          return (
            <Marker key={row.propertyId} position={[row.latitude, row.longitude]}>
              <Popup>
                <div
                  data-testid={`founding-popup-${row.propertyId}`}
                  className="space-y-1 font-sans"
                >
                  <p className="font-medium">{row.propertyName}</p>
                  <p className="text-xs text-muted-foreground">{row.ownerName}</p>
                  <FoundingStatusBadge
                    status={deriveFoundingStatus(row.founding)}
                    testId={`founding-popup-status-${row.propertyId}`}
                  />
                  <p className="text-xs">
                    {t("founding.table.tier")}: {row.currentTier ?? "—"}
                  </p>
                  <p className="text-xs">
                    {t("founding.table.discount")}: {pct === null ? "—" : `${pct}%`}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
